import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)
  const authStateReceivedRef = useRef(false)

  const loadProfile = async (userId) => {
    if (!userId) return
    setProfileLoading(true)
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('user_id', userId)?.single()
      if (!error) setUserProfile(data)
    } catch (error) {
      console.error('Profile load error:', error)
    } finally {
      setProfileLoading(false)
    }
  }

  const clearProfile = () => {
    setUserProfile(null)
    setProfileLoading(false)
  }

  useEffect(() => {
    // Subscribe to auth state changes FIRST
    // onAuthStateChange is the authoritative source — always update user from it
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange((event, session) => {
      authStateReceivedRef.current = true
      const currentUser = session?.user ?? null
      setUser(currentUser)
      setLoading(false)

      if (currentUser) {
        setTimeout(() => loadProfile(currentUser?.id), 0)
      } else {
        clearProfile()
      }
    })

    // getSession as fallback: only sets state if onAuthStateChange hasn't fired yet
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      if (!authStateReceivedRef?.current) {
        const currentUser = session?.user ?? null
        setUser(currentUser)
        setLoading(false)
        if (currentUser) {
          setTimeout(() => loadProfile(currentUser?.id), 0)
        }
      }
    })

    return () => subscription?.unsubscribe()
  }, [])

  // Auth methods
  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({ email, password })
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signInWithGoogle = async (redirectTo) => {
    try {
      const { data, error } = await supabase?.auth?.signInWithOAuth({
        provider: 'google',
        options: { redirectTo }
      })
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signOut = async () => {
    try {
      const { error } = await supabase?.auth?.signOut()
      if (!error) {
        setUser(null)
        clearProfile()
      }
      return { error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } }
    
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('user_id', user?.id)?.select()?.single()
      if (!error) setUserProfile(data)
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    signIn,
    signInWithGoogle,
    signOut,
    updateProfile,
    isAuthenticated: !!user
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
